// Modules
import { useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate } from "react-router-dom"
import { CircularProgress } from "@mui/material"

// Constants
import PATHS from "~constants/paths"
import { ERROR_MESSAGES } from "~constants/errorMessages"
import { APP_VERSION } from "~constants/config"

// Utils
import { Validation } from "~utils/regexValidations"

// Components
import Logo from "~components/Logo"
import Input from "~app/Input"
import { FormSubmit } from "~styled/form"
import {
  AuthForm,
  AuthLink,
  AuthError,
  AuthLayout,
  AuthHeading,
  AuthSubtitle,
  AuthContainer,
  AuthVersionBadge,
} from "~styled/auth"

// Context
import { useAuthContext } from "~context/authContext"

// Custom Hooks
import usePublicPath from "~hooks/usePublicPath"

const LoginPage = () => {
  const navigate = useNavigate()

  // Get from context
  const { handleLogin } = useAuthContext()

  // Using custom hooks
  const loggedIn = usePublicPath()

  // Set component state
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [loginError, setLoginError] = useState<string>(null!)

  // Form definition
  const {
    formState: { errors },
    register,
    clearErrors,
    handleSubmit,
  } = useForm({
    defaultValues: { username: "", password: "" },
  })

  // Method to handle the submit event
  const onSubmit = async (data: { username: string; password: string }) => {
    // Cancel if it still loading
    if (isLoading) return

    // Reset login error and set loading status
    setIsLoading(true)
    setLoginError(null!)

    try {
      await handleLogin(data)
    } catch (err: any) {
      setLoginError(err.message)
    }

    setIsLoading(false)
  }

  // Method to redirect to the forgot password
  const redirectToForgotPassword = (event: React.MouseEvent) => {
    event.preventDefault()
    navigate(PATHS.forgotPassword)
  }

  return (
    !loggedIn && (
      <AuthLayout>
        <AuthContainer>
          <Logo />

          <AuthHeading>Welcome back</AuthHeading>
          <AuthSubtitle>Sign in to your Perk Central account</AuthSubtitle>

          <AuthForm onSubmit={handleSubmit(onSubmit)}>
            <Input
              name="username"
              type="email"
              label="Email"
              register={register("username", {
                required: true,
                pattern: {
                  value: Validation.email,
                  message: ERROR_MESSAGES.INVALID_EMAIL,
                },
              })}
              onChange={() => clearErrors()}
              errorMessage={
                errors.username &&
                (errors.username?.message || ERROR_MESSAGES.FIELD_REQUIRED)
              }
            />

            <Input
              name="password"
              type="password"
              label="Password"
              register={register("password", { required: true })}
              onChange={() => clearErrors()}
              errorMessage={
                errors.password &&
                (errors.password?.message || ERROR_MESSAGES.FIELD_REQUIRED)
              }
            />

            <FormSubmit
              type="submit"
              disabled={isLoading}
              className={isLoading ? "loading" : ""}
            >
              {isLoading ? (
                <>
                  <CircularProgress size={18} className="loading-icon" />
                  <span>Signing in…</span>
                </>
              ) : (
                <span>Log In</span>
              )}
            </FormSubmit>

            {!loginError ? null : <AuthError>{loginError}</AuthError>}

            {/* Forgot Password */}
            <AuthLink onClick={redirectToForgotPassword}>
              Forgot password?
            </AuthLink>
          </AuthForm>
          <AuthVersionBadge>{APP_VERSION}</AuthVersionBadge>
        </AuthContainer>
      </AuthLayout>
    )
  )
}

export default LoginPage
